import {
  $applyNodeReplacement,
  $isParagraphNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  ParagraphNode,
  SerializedParagraphNode,
  Spread,
} from "lexical";

export type SerializedStyledParagraphNode = Spread<{ style?: string }, SerializedParagraphNode>;

/**
 * Extends ParagraphNode to render the element's __style property to DOM and HTML export.
 * ParagraphNode stores __style internally but never applies it to the DOM element or
 * includes it in exportDOM/exportJSON. This subclass fixes that so paragraph-level
 * CSS properties like line-height work end-to-end.
 */
export class StyledParagraphNode extends ParagraphNode {
  static getType(): string {
    return "styled-paragraph";
  }

  static clone(node: StyledParagraphNode): StyledParagraphNode {
    return new StyledParagraphNode(node.__key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    const style = this.__style;
    if (style) {
      dom.style.cssText = style;
    }
    return dom;
  }

  updateDOM(prevNode: StyledParagraphNode, dom: HTMLElement, _config: EditorConfig): boolean {
    const prevStyle = prevNode.__style;
    const nextStyle = this.__style;
    if (prevStyle !== nextStyle) {
      dom.style.cssText = nextStyle || "";
      const formatType = this.getFormatType();
      if (formatType) {
        dom.style.textAlign = formatType;
      }
    }
    return false;
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const output = super.exportDOM(editor);
    const { element } = output;
    if (element instanceof HTMLElement) {
      const style = this.getStyle();
      if (style) {
        const existing = element.getAttribute("style") || "";
        element.setAttribute("style", existing ? `${existing}; ${style}` : style);
      }
    }
    return output;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      p: () => ({
        conversion: convertStyledParagraphElement,
        priority: 1,
      }),
    };
  }

  exportJSON(): SerializedStyledParagraphNode {
    const json = super.exportJSON();
    const style = this.getStyle();
    return {
      ...json,
      type: "styled-paragraph",
      ...(style ? { style } : {}),
    };
  }

  static importJSON(serializedNode: SerializedStyledParagraphNode): StyledParagraphNode {
    const node = $createStyledParagraphNode();
    node.setFormat(serializedNode.format as ElementFormatType);
    node.setIndent(serializedNode.indent || 0);
    node.setDirection(serializedNode.direction || null);
    node.setTextFormat(serializedNode.textFormat || 0);
    node.setTextStyle(serializedNode.textStyle || "");
    if (serializedNode.style) {
      node.setStyle(serializedNode.style);
    }
    return node;
  }
}

function convertStyledParagraphElement(element: HTMLElement): DOMConversionOutput {
  const node = $createStyledParagraphNode();
  if (element.style) {
    node.setFormat(element.style.textAlign as ElementFormatType);
    const lineHeight = element.style.lineHeight;
    if (lineHeight) {
      node.setStyle(`line-height: ${lineHeight}`);
    }
  }
  return { node };
}

export function $createStyledParagraphNode(): StyledParagraphNode {
  return $applyNodeReplacement(new StyledParagraphNode());
}

export function $isStyledParagraphNode(node: LexicalNode | null | undefined): node is StyledParagraphNode {
  return $isParagraphNode(node);
}
